import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { alpha, darken } from '@mui/material/styles';
import {
  Box, Typography, Grid, Chip, Avatar, Button, Divider, Stack,
  Tabs, Tab, CircularProgress, Pagination, Snackbar, Alert,
  Dialog, DialogContent, Radio, RadioGroup, FormControlLabel, TextField, IconButton, Rating,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaptopIcon from '@mui/icons-material/Laptop';
import HomeIcon from '@mui/icons-material/Home';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { getProviderStats, getProviderBookings, getProviderCalendar, updateBookingStatus, completeBooking, getClientStats, getClientBookings, cancelClientBooking, solicitarCambio, responderCambio, getBookedSlots, addExtra, removeExtra, pagarExtras, reportarProblema, crearAclaracion } from '../../../actions/bookings';
import { getPagoBooking, verificarPagoBooking } from '../../../actions/payments';
import { getMisTickets } from '../../../actions/tickets';
import { startChat } from '../../../actions/chat';
import { submitReview } from '../../../actions/reviews';
import { getPublicServiceDetail } from '../../../actions/profile';
import { urlImg } from '../../../components/common/helpers';
import AvisoCobro from '../../../components/common/AvisoCobro';
import BookingCalendar from '../../../components/booking/BookingCalendar';
import BookingSchedule from '../../../components/booking/BookingSchedule';
import ReporteServicioModal from '../../../components/booking/ReporteServicioModal';
import DetalleReservaModal from '../../../components/booking/DetalleReservaModal';
// «Hoy» y las horas que faltan para una cita, con las mismas reglas que el servidor (no con la zona del dispositivo ni con UTC)
import { hoyEnMexico, instanteDeCita } from '../../../components/booking/horaMexico';
import { dinero } from '../../../utils/dinero';

// Botones de descarga de la factura (CFDI): aparecen en el historial cuando el
// superadmin ya subió el PDF y el XML. Reutilizado por ambas tarjetas.
const FacturaBotones = ({ factura }) => (
  <>
    {factura?.xml?.url && (
      <Button size="small" variant="contained" disableElevation
        component="a" href={factura.xml.url} target="_blank" rel="noopener noreferrer"
        sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'success.main', color: 'white', fontSize: '12px', minWidth: 0, px: 2,
          '&:hover': { bgcolor: 'success.dark' } }}>
        XML
      </Button>
    )}
    {factura?.pdf?.url && (
      <Button size="small" variant="contained" color="secondary" disableElevation
        component="a" href={factura.pdf.url} target="_blank" rel="noopener noreferrer"
        sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', minWidth: 0, px: 2 }}>
        PDF
      </Button>
    )}
  </>
);

// Colores de cada estado, del tema: el texto en el tono oscuro de su color o, sobre el verde claro y los azules, en el azul oscuro (como las
// fichas «Confirmado» del resto de la web)
const STATUS_COLOR = {
  pendiente:  { bgcolor: 'warning.main', color: 'warning.oscuro', label: 'Pendiente' },
  confirmada: { bgcolor: 'success.claro', color: 'primary.main', label: 'Confirmado' },
  cancelada:  { bgcolor: (t) => alpha(t.palette.error.main, 0.25), color: 'error.oscuro', label: 'Cancelado' },
  completada: { bgcolor: (t) => alpha(t.palette.secondary.main, 0.2), color: 'primary.main', label: 'Completado' },
  expirada:   { bgcolor: 'grey.100', color: 'text.secondary', label: 'Caducado' }
};

const minutesToLabel = (timeStr, extraMin = 0) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + (m || 0) + extraMin;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
  return `${String(h12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${ampm}`;
};

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  return `${day} de ${MONTH_NAMES[month - 1]}, ${year}`;
};

const formatCompactNumber = (n) =>
  new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

const formatCompactCurrency = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

const CONTRATADOS_STATS = [
  { key: 'pendiente',  label: 'Pendientes',  color: 'warning.main' },
  { key: 'activo',     label: 'Activos',     color: 'success.main' },
  { key: 'proximo',    label: 'Próximos',    color: 'primary.main' },
  { key: 'finalizado', label: 'Finalizados', color: 'secondary.main' },
  { key: 'caducado',   label: 'Caducos',     color: 'text.secondary' },
  { key: 'cancelado',  label: 'Cancelados',  color: 'error.main' },
];

const MOTIVOS = [
  { value: 'disponibilidad', label: 'Falta de disponibilidad', desc: 'Requiere más tiempo del solicitado.', emoji: '🔑' },
  { value: 'ubicacion',      label: 'Ubicación',               desc: 'Está fuera de mi radio de cobertura.', emoji: '📍' },
  { value: 'recursos',       label: 'Recursos',                desc: 'No cuento con el equipo necesario.', emoji: '🔧' },
  { value: 'otra',           label: 'Otra',                    desc: 'Cuéntanos por qué', emoji: '🚫' },
];

const DeclineModal = ({ open, onClose, onConfirm, loading }) => {
  const [razon, setRazon] = useState('disponibilidad');
  const [textoOtra, setTextoOtra] = useState('');

  const handleConfirm = () => {
    const motivo = razon === 'otra'
      ? textoOtra.trim()
      : MOTIVOS.find(m => m.value === razon)?.label || razon;
    onConfirm(motivo);
  };

  const handleClose = () => {
    setRazon('disponibilidad');
    setTextoOtra('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        {/* Ícono */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningAmberIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1.5 }}>
          Estás por declinar un servicio
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          Lo cual significa que no podrás brindarlo al usuario,<br />
          hazle saber el por qué seleccionando la opción más adecuada
        </Typography>

        {razon !== 'otra' ? (
          <RadioGroup value={razon} onChange={e => setRazon(e.target.value)}>
            {MOTIVOS.map(m => (
              <Box key={m.value} onClick={() => setRazon(m.value)}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, px: 1, borderRadius: '8px', cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.50' } }}>
                <Radio value={m.value} size="small" sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' }, pt: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {m.emoji} {m.label}
                    {m.value === 'otra' && <Typography component="span" variant="body2" color="error.oscuro"> (Mensaje o nota para el usuario)</Typography>}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">({m.desc})</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        ) : (
          <Box sx={{ border: 1, borderColor: 'sutil', borderRadius: '12px', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                🚫 <Typography component="span" color="error.oscuro">Otra (Mensaje o nota para el usuario)</Typography>
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Hazle saber al usuario el motivo de la declinación, tal vez puedan llegar a un acuerdo
            </Typography>
            <TextField
              multiline rows={4} fullWidth
              placeholder="Ej. Tengo un servicio del otro lado de la ciudad antes y es posible que no llegue a tiempo..."
              value={textoOtra}
              onChange={e => setTextoOtra(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.85rem' } }}
            />
            <Button size="small" onClick={() => setRazon('disponibilidad')}
              sx={{ mt: 1, textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}>
              ← Volver a opciones
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button fullWidth variant="contained" onClick={handleClose}
            sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: 'primary.dark' } }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" color="error" onClick={handleConfirm} disabled={loading || (razon === 'otra' && !textoOtra.trim())}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Declinar servicio'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const getPagosStats = (stats) => [
  { key: 'recibir', label: 'Completados este mes',      color: 'success.main', value: stats.recibir ?? 0, esMoneda: true },
  { key: 'cobrar',  label: 'Por cobrar (confirmados)',  color: 'warning.main', value: stats.cobrar  ?? 0, esMoneda: true },
  { key: 'total',   label: 'Servicios completados mes', color: 'secondary.main', value: stats.totalMes ?? 0, esMoneda: false },
];

const toFechaStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// directo: el cambio se aplica sin aprobación (cliente en reserva aún pendiente).
// esProveedor: quien modifica es el proveedor y la propuesta la aprueba el cliente.
const ModificarModal = ({ open, onClose, onConfirm, loading, booking, directo = false, esProveedor = false }) => {
  const [service, setService] = useState(null);
  const [loadingService, setLoadingService] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);

  // Cargar disponibilidad del servicio al abrir
  useEffect(() => {
    if (!open) return;
    setSelectedDate(null);
    setSelectedTime(null);
    setBookedSlots([]);
    const proveedorId = booking.proveedor?._id || booking.proveedor;
    if (!proveedorId || !booking.servicio) return;
    setLoadingService(true);
    getPublicServiceDetail(proveedorId, booking.servicio)
      .then(res => setService(res?.service || null))
      .finally(() => setLoadingService(false));
  }, [open, booking.proveedor, booking.servicio]);

  // Cargar slots ocupados al cambiar de fecha
  useEffect(() => {
    if (!selectedDate || !booking.servicio) return;
    setSelectedTime(null);
    getBookedSlots(booking.servicio, toFechaStr(selectedDate)).then(setBookedSlots);
  }, [selectedDate, booking.servicio]);

  const handleClose = () => { setSelectedDate(null); setSelectedTime(null); onClose(); };

  const fechaStr = selectedDate ? toFechaStr(selectedDate) : '';
  const isValid = fechaStr && selectedTime && (fechaStr !== booking.fecha || selectedTime !== booking.hora);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
          {directo ? 'Modificar fecha de la reserva' : esProveedor ? 'Proponer cambio de fecha' : 'Solicitar cambio de fecha'}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          {directo ? (
            <>Elige una nueva fecha y horario dentro de la disponibilidad del proveedor.<br />
            Como aún no acepta tu solicitud, el cambio se aplica de inmediato y se le avisa.</>
          ) : esProveedor ? (
            <>Elige una nueva fecha y horario dentro de tu disponibilidad.<br />
            El cliente recibirá tu propuesta y deberá aceptarla.
            {booking.status === 'pendiente' && <> Si la acepta, la reserva quedará confirmada en automático con la nueva fecha.</>}</>
          ) : (
            <>Elige una nueva fecha y horario dentro de la disponibilidad del proveedor.<br />
            El proveedor recibirá tu solicitud y deberá aprobarla.</>
          )}
        </Typography>

        <Box sx={{ bgcolor: 'sutil', borderRadius: '12px', p: 2, mb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Fecha y hora actuales</Typography>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
          </Typography>
        </Box>

        {loadingService ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress sx={{ color: 'secondary.main' }} />
          </Box>
        ) : (
          <Box sx={{ border: 1, borderColor: 'sutil', borderRadius: '16px', p: 3, mb: 4 }}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <BookingCalendar
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                  hideContainer
                  disponibilidad={service?.disponibilidad}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {selectedDate ? (
                  <BookingSchedule
                    selectedTime={selectedTime}
                    onTimeSelect={setSelectedTime}
                    hideContainer
                    service={service}
                    selectedDate={selectedDate}
                    bookedSlots={bookedSlots}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selecciona primero una fecha disponible.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={() => onConfirm(booking._id, fechaStr, selectedTime)}
            disabled={loading || !isValid}
            sx={{ bgcolor: 'warning.main', color: 'warning.oscuro', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: (t) => darken(t.palette.warning.main, 0.06) }, '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.warning.main, 0.3), color: 'text.disabled' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : directo ? 'Guardar cambio' : esProveedor ? 'Enviar propuesta' : 'Enviar solicitud'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const AcceptModal = ({ open, onClose, onConfirm, loading, booking }) => {
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, cliente } = booking;
  const clientName = cliente ? `${cliente.name} ${cliente.apellido}` : 'Cliente';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 2 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
          Confirmar reserva
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, lineHeight: 1.6 }}>
          Estás a punto de comprometerte con este servicio.<br />
          El cliente será notificado de inmediato.
        </Typography>

        <Box sx={{ bgcolor: 'sutil', borderRadius: '12px', p: 2.5, mb: 4 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Resumen
          </Typography>
          <Stack spacing={1.2}>
            {[
              { label: 'Cliente',    value: clientName },
              { label: 'Servicio',   value: servicioNombre },
              { label: 'Fecha',      value: formatDate(fecha) },
              { label: 'Horario',    value: `${minutesToLabel(hora)} — ${minutesToLabel(hora, duracionMinutos)}` },
              { label: 'Modalidad',  value: modalidad },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 64 }}>{label}:</Typography>
                <Typography variant="caption" fontWeight="bold" color="primary.main">{value}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" onClick={onConfirm} disabled={loading}
            sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar reserva'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CompletarModal = ({ open, onClose, onConfirm, loading, booking }) => {
  const [pin, setPin] = useState('');
  useEffect(() => { if (open) setPin(''); }, [open]);
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  // Los extras los paga el cliente desde su reserva ANTES de poder completar
  const extrasRestante = Math.round((totalExtras - (booking.pagoExtra?.pagado || 0)) * 100) / 100;
  const extrasSinPagar = !!booking.pago && extrasRestante > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        </Box>
        <Typography variant="h6" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
          ¿Confirmar servicio realizado?
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          <strong>{booking.servicioNombre}</strong> — {formatDate(booking.fecha)}
        </Typography>

        {/* PIN que el cliente comparte al terminar (su aceptación del servicio y extras).
            Reservas confirmadas antes de esta función no tienen PIN. */}
        {booking.tienePin && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 1.5 }}>
              Pídele al cliente el <strong>PIN de confirmación</strong> que aparece en su reserva.
            </Typography>
            <TextField
              fullWidth
              placeholder="PIN de 4 dígitos"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontWeight: 900, fontSize: 22, letterSpacing: 8 } }}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {totalExtras > 0 && (
          <Box sx={{
            bgcolor: extrasSinPagar ? ((t) => alpha(t.palette.warning.main, 0.12)) : 'grey.50',
            border: 1, borderColor: extrasSinPagar ? 'warning.main' : 'sutil',
            borderRadius: '10px', p: 1.5, mb: 2
          }}>
            <Typography variant="caption" color={extrasSinPagar ? 'warning.oscuro' : booking.pago ? 'success.oscuro' : 'text.secondary'}>
              {extrasSinPagar
                ? <>El cliente aún no paga <strong>{dinero(extrasRestante)} MXN de extras</strong>. Pídele pagarlos desde su reserva; hasta entonces no podrás completar el servicio.</>
                : booking.pago
                ? <>✓ Los <strong>{dinero(totalExtras)} MXN de extras</strong> ya están pagados.</>
                : <>Acuerda el cobro de los <strong>{dinero(totalExtras)} MXN de extras</strong> directamente con el cliente.</>}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disabled={loading || (booking.tienePin && pin.length < 4)} onClick={() => onConfirm(pin)}
            sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Modal del proveedor para agregar un cargo extra durante el servicio
const ExtraModal = ({ open, onClose, onConfirm, loading }) => {
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [errores, setErrores] = useState({});
  useEffect(() => { if (open) { setConcepto(''); setMonto(''); setErrores({}); } }, [open]);

  const guardar = () => {
    const nuevos = {};
    if (!concepto.trim()) nuevos.concepto = 'Describe el extra';
    if (!(parseFloat(monto) > 0)) nuevos.monto = 'Monto inválido';
    if (Object.keys(nuevos).length) { setErrores(nuevos); return; }
    onConfirm(concepto.trim(), parseFloat(monto));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Typography variant="h6" fontWeight="900" color="primary.main" sx={{ mb: 1 }}>
          Agregar extra
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          El cliente lo verá de inmediato en su reserva y se cobrará al completar el
          servicio, cuando te comparta su PIN (esa es su aceptación).
        </Typography>
        <TextField fullWidth label="Concepto" placeholder="Ej. Material adicional" value={concepto}
          onChange={(e) => { setConcepto(e.target.value); setErrores(p => ({ ...p, concepto: '' })); }}
          error={!!errores.concepto} helperText={errores.concepto || ''} size="small" sx={{ mb: 2 }} />
        <TextField fullWidth label="Monto (MXN)" placeholder="0.00" value={monto}
          onChange={(e) => { setMonto(e.target.value.replace(/[^\d.]/g, '')); setErrores(p => ({ ...p, monto: '' })); }}
          error={!!errores.monto} helperText={errores.monto || ''} size="small"
          inputProps={{ inputMode: 'decimal' }} sx={{ mb: parseFloat(monto) > 0 ? 0.75 : 2.5 }} />
        {/* El extra también paga el 10% de comisión de la plataforma */}
        {parseFloat(monto) > 0 && (() => {
          const c = parseFloat(monto);
          const comision = Math.round(c * 10) / 100;
          const neto = Math.round((c - comision) * 100) / 100;
          return (
            <Typography variant="caption" sx={{ display: 'block', mb: 2.5, color: 'success.oscuro' }}>
              Recibirás <strong>{dinero(neto)}</strong> —
              comisión de la plataforma 10% ({dinero(comision)}).
            </Typography>
          );
        })()}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disabled={loading} onClick={guardar}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700 }}>
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Agregar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CancelarConfirmadaModal = ({ open, onClose, onConfirm, loading, booking, esProveedor, penalizacion }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
    <DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'error.oscuro', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CancelOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
        </Box>
      </Box>

      <Typography variant="h6" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
        ¿Cancelar servicio confirmado?
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
        <strong>{booking?.servicioNombre}</strong> — {formatDate(booking?.fecha)}
      </Typography>

      {/* Resultado según la política de cancelación */}
      {esProveedor ? (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.3), border: 1, borderColor: 'warning.main', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="caption" color="warning.oscuro" sx={{ lineHeight: 1.6 }}>
            El pago del cliente se le reembolsará por completo. Cancelar servicios
            confirmados afecta la confianza en tu perfil; hazlo solo si es necesario.
          </Typography>
        </Box>
      ) : penalizacion ? (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.3), border: 1, borderColor: 'warning.main', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="body2" fontWeight="900" color="warning.oscuro" sx={{ mb: 0.5 }}>
            ⚠️ Cancelación con penalización del {penalizacion.pct}%
          </Typography>
          <Typography variant="caption" color="warning.oscuro" sx={{ lineHeight: 1.6 }}>
            Por cancelar con menos de 24 horas de anticipación, de tu pago de{' '}
            <strong>{dinero(penalizacion.monto)} MXN</strong> se te reembolsarán{' '}
            <strong>{dinero(penalizacion.devuelto)} MXN</strong>; el resto se entrega al
            proveedor como compensación por el tiempo que apartó.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.2), border: 1, borderColor: 'success.claro', borderRadius: '12px', p: 2, mb: 2.5 }}>
          <Typography variant="caption" color="success.oscuro" sx={{ lineHeight: 1.6 }}>
            Cancelación sin costo. {booking?.pago?.status === 'retenido'
              ? 'Se te reembolsará el total de tu pago.'
              : 'No se ha realizado ningún cobro.'}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 2.5 }}>
        {esProveedor
          ? 'El cliente será notificado de la cancelación.'
          : 'El proveedor será notificado de la cancelación.'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={onClose}
          sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
          Volver
        </Button>
        <Button fullWidth variant="contained" color="error" disabled={loading} onClick={onConfirm}
          sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
            '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Sí, cancelar'}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

const CalificarModal = ({ open, onClose, onConfirm, loading, esProveedor }) => {
  const [rating, setRating] = useState(0);
  const [comentario, setComentario] = useState('');

  const handleClose = () => {
    setRating(0);
    setComentario('');
    onClose();
  };

  const handleSubmit = () => {
    if (!rating) return;
    onConfirm(rating, comentario);
    setRating(0);
    setComentario('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Typography variant="h6" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 0.5 }}>
          {esProveedor ? 'Calificar al cliente' : 'Calificar el servicio'}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2.5 }}>
          {esProveedor
            ? 'Tu opinión sobre el cliente ayuda a la comunidad.'
            : 'Tu opinión ayuda a otros usuarios a elegir mejor.'}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Rating
            value={rating}
            onChange={(_, v) => setRating(v)}
            size="large"
            sx={{ fontSize: '2.5rem', color: 'primary.main' }}
          />
        </Box>

        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder={esProveedor ? 'Cuéntanos cómo fue la experiencia con el cliente (opcional)' : 'Cuéntanos cómo fue tu experiencia (opcional)'}
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={handleClose}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" color="secondary" disabled={loading || !rating} onClick={handleSubmit}
            sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
              '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.35) } }}>
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Publicar'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// A dónde ir en una reserva a domicilio: la foto de la dirección que guardó el servidor al reservar (direccionServicio), como en la
// app. Las que se hicieron en la web antes de que la mandara no la tienen: al proveedor se le dice que la pida.
const DireccionServicio = ({ booking, esProveedor = false }) => {
  const d = booking.direccionServicio || {};
  const lugar = d.direccion || [d.calle, d.numeroExt, d.colonia, d.municipio, d.estado].filter(Boolean).join(', ');
  if (!lugar) {
    return booking.modalidad === 'Domicilio' && esProveedor ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Sin dirección registrada: pídesela al cliente por mensaje.
      </Typography>
    ) : null;
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1.5 }}>
      <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary', mt: '1px', flexShrink: 0 }} />
      <Typography variant="caption" color="text.secondary">
        {d.nombre ? `${d.nombre}: ` : ''}{lugar}{d.numeroInt ? `, int. ${d.numeroInt}` : ''}{d.referencia ? ` · Ref: ${d.referencia}` : ''}
      </Typography>
    </Box>
  );
};

const BookingCard = ({ booking, tieneTicket, onAccept, onDecline, onComplete, onModificar, onResponderCambio, onAddExtra, onRemoveExtra, onReportar, onAclarar, loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [completarOpen, setCompletarOpen] = useState(false);
  const [modificarOpen, setModificarOpen] = useState(false);
  const [disputaOpen, setDisputaOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [aclaracionOpen, setAclaracionOpen] = useState(false);
  const [cancelConfirmadaOpen, setCancelarConfirmadaOpen] = useState(false);
  const [extraOpen, setExtraOpen] = useState(false);
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, nota, status, cliente } = booking;
  const clientName = cliente ? `${cliente.name} ${cliente.apellido}` : 'Cliente';
  const avatarSrc = cliente?.avatar
    ? `${urlImg}/users/${cliente._id}/${cliente.avatar}?v=${cliente.versionAvatar || 0}`
    : null;

  const st = STATUS_COLOR[status] || STATUS_COLOR.pendiente;
  const isPending = status === 'pendiente';
  const today = hoyEnMexico();
  const canComplete = status === 'confirmada' && fecha <= today;
  const canCancelConfirmada = status === 'confirmada' && fecha > today;
  const canModify = ['pendiente', 'confirmada'].includes(status) && !booking.modificacion?.solicitada && fecha >= today;
  // Con solicitadaPor vacío (solicitudes viejas) el cambio lo pidió el cliente
  const cambioDelProveedor = booking.modificacion?.solicitadaPor === 'proveedor';
  // Todo se paga en la plataforma: con SPEI u OXXO sin pagar no se da el servicio, y una solicitud de antes hecha sin pago ya no se acepta
  const pagoPendiente = status === 'confirmada' && booking.pago?.status === 'pendiente';
  const sinPago = isPending && !booking.pago;

  // Estado del pago de extras: el cliente los paga desde su reserva y hasta entonces
  // se puede completar el servicio
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  const extrasEnProceso = !!booking.pagoExtra?.enProceso;
  const extrasRestante = Math.round((totalExtras - (booking.pagoExtra?.pagado || 0)) * 100) / 100;

  // Disputa: cuando el servicio ya debió ocurrir y aún no se completa, o durante la
  // semana de retención (completado con el pago aún en custodia, sin dispersar).
  const servicioPasado = instanteDeCita(fecha, hora) <= Date.now();
  const disputaAbierta = !!booking.disputa?.abierta;
  const enRetencion = status === 'completada' && booking.pago?.status === 'retenido' && !disputaAbierta;
  // Solo se puede reportar/aclarar dentro de los últimos 3 meses (por fecha del servicio)
  const canReport = ((status === 'confirmada' && servicioPasado) || enRetencion) && !disputaAbierta && dentroVentanaReporte(fecha);
  // Historial: reserva finalizada → botón "Ver detalles" (modal) y, si hay factura, PDF/XML
  const esFinalizado = status === 'completada';
  const facturaEmitida = booking.factura?.estado === 'emitida';

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'sutil', p: 3, mb: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="900" color="primary.main">
            {servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={st.label} size="small"
            sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }} />
          {/* Estado del pago: clave para no dar el servicio sin cobro confirmado (SPEI/tienda) */}
          {booking.pago && PAGO_STATUS[booking.pago.status] && (
            <Chip
              label={`${PAGO_STATUS[booking.pago.status].label} · ${dinero(booking.pago.monto)}`}
              size="small"
              sx={{
                bgcolor: PAGO_STATUS[booking.pago.status].bgcolor,
                color: PAGO_STATUS[booking.pago.status].color,
                fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px'
              }} />
          )}
        </Box>
      </Box>

      {/* Nota */}
      {nota && (
        <Typography variant="body2" color="text.secondary" sx={{
          mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          <strong>Mensaje del usuario: </strong>{nota}
        </Typography>
      )}

      {/* Info row */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {minutesToLabel(hora)} - {minutesToLabel(hora, duracionMinutos)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {modalidad === 'Remoto'
            ? <LaptopIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            : modalidad === 'Domicilio'
            ? <HomeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            : <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          <Typography variant="caption" color="text.secondary">{modalidad}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">{formatDate(fecha)}</Typography>
        </Box>
      </Box>

      <DireccionServicio booking={booking} esProveedor />

      {/* Todo se paga en la plataforma: sin el pago del cliente no se da el servicio */}
      {pagoPendiente && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Esperando el pago del cliente ({booking.pago.metodo === 'oxxo' ? 'OXXO' : 'SPEI'})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            No des el servicio hasta que se confirme: te avisamos cuando llegue.
            {booking.pago.vence ? ` Si no paga antes del ${fmtVence(booking.pago.vence)}, la reserva se cancela sola.` : ' Si no paga a tiempo, la reserva se cancela sola.'}
          </Typography>
        </Box>
      )}
      {sinPago && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            Esta solicitud se hizo sin pago
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ya no se puede aceptar: todas las reservas se pagan en la plataforma. Declínala para liberar el horario; el cliente puede volver
            a reservar pagando.
          </Typography>
        </Box>
      )}

      {/* Disputa abierta: el pago está congelado hasta que la plataforma resuelva */}
      {disputaAbierta && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.25), border: 1, borderColor: 'error.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="error.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            🚩 Disputa abierta {booking.disputa.abiertaPor === 'proveedor' ? '(la abriste tú)' : '(la abrió el cliente)'}
          </Typography>
          <Typography variant="caption" color="error.oscuro">
            El pago está congelado mientras el equipo de la plataforma revisa el caso.
            Te avisaremos la resolución.
          </Typography>
        </Box>
      )}

      {/* Banner solicitud de cambio: del cliente (responder) o propia (en espera) */}
      {booking.modificacion?.solicitada && !cambioDelProveedor && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 2, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 1 }}>
            ⚡ El cliente solicita cambiar la fecha
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Fecha actual</Typography>
              <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ display: 'block' }}>
                {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
              </Typography>
            </Box>
            <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center', fontSize: '1.1rem' }}>→</Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">Fecha solicitada</Typography>
              <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block' }}>
                {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" color="error" disabled={loading}
              onClick={() => onResponderCambio(booking._id, false)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
              Rechazar cambio
            </Button>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, true)}
              sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
              Aceptar cambio
            </Button>
          </Box>
        </Box>
      )}

      {booking.modificacion?.solicitada && cambioDelProveedor && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Propusiste una nueva fecha, en espera del cliente
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Propusiste: <Box component="strong" sx={{ color: 'warning.oscuro' }}>
              {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
            </Box>
          </Typography>
        </Box>
      )}

      {/* Extras agregados durante el servicio (editable mientras esté confirmado) */}
      {booking.extras?.length > 0 && (
        <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'sutil', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
            Extras del servicio
          </Typography>
          {booking.extras.map((e) => (
            <Box key={e._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">{e.concepto}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="primary.main">{dinero(e.monto)}</Typography>
                {status === 'confirmada' && (
                  <IconButton size="small" disabled={loading} onClick={() => onRemoveExtra(booking._id, e._id)}
                    sx={{ p: 0.25, color: 'error.oscuro' }}>
                    <CancelOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
          <Divider sx={{ my: 0.5, borderColor: 'sutil' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={700} color="primary.main">Total extras</Typography>
            <Typography variant="caption" fontWeight={900} color="primary.main">{dinero(totalExtras)}</Typography>
          </Box>

          {/* El cliente paga los extras desde su reserva; hasta entonces no se puede completar */}
          {booking.pago && status === 'confirmada' && (
            extrasEnProceso ? (
              <Typography variant="caption" color="warning.oscuro" sx={{ display: 'block', mt: 0.5 }}>
                ⏳ El cliente generó su referencia de pago; en cuanto se confirme podrás finalizar.
              </Typography>
            ) : extrasRestante <= 0 ? (
              <Typography variant="caption" color="success.oscuro" sx={{ display: 'block', mt: 0.5 }}>
                ✓ Extras pagados por el cliente. Ya puedes finalizar el servicio con su PIN.
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.oscuro" sx={{ display: 'block', mt: 0.5 }}>
                El cliente debe pagar los extras desde su reserva antes de que puedas finalizar el servicio.
              </Typography>
            )
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.5, borderColor: 'sutil' }} />

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box
          onClick={() => cliente?._id && navigate(`/user/cliente/${cliente._id}`)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: cliente?._id ? 'pointer' : 'default',
            '&:hover .client-name': cliente?._id ? { textDecoration: 'underline' } : {}
          }}
        >
          <Typography variant="caption" color="text.secondary">Info del cliente</Typography>
          <Avatar src={avatarSrc} sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>
            {!avatarSrc && clientName.charAt(0)}
          </Avatar>
          <Typography className="client-name" variant="body2" fontWeight="bold" color="primary.main">{clientName}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tieneTicket && (
            <Button size="small" variant="outlined"
              startIcon={<SupportAgentOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/user/tickets?booking=${booking._id}`)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', borderColor: 'secondary.main', color: 'primary.main',
                '&:hover': { borderColor: 'secondary.dark', bgcolor: (t) => alpha(t.palette.secondary.main, 0.12) } }}>
              Ver ticket
            </Button>
          )}
          {esFinalizado ? (
            <>
              {facturaEmitida && <FacturaBotones factura={booking.factura} />}
              <Button size="small" variant="contained" disableElevation
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDetalleOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'primary.main', fontSize: '12px',
                  '&:hover': { bgcolor: 'primary.dark' } }}>
                Ver detalles
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined"
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
              disabled={!cliente?._id}
              onClick={() => dispatch(startChat(cliente._id, booking.servicio, 'service'))}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'success.main', color: 'success.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => alpha(t.palette.success.main, 0.1), borderColor: 'success.oscuro' } }}>
              Contactar usuario
            </Button>
          )}
          {canModify && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => setModificarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'warning.main', color: 'warning.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => darken(t.palette.warning.main, 0.06) }, '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.warning.main, 0.3) } }}>
              Modificar fecha
            </Button>
          )}
          {isPending && (
            <>
              <Button size="small" variant="contained" color="error" disabled={loading}
                startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDeclineOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                  '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
                Declinar
              </Button>
              {!sinPago && <Button size="small" variant="contained" disabled={loading}
                startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                onClick={() => setAcceptOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'primary.main', fontSize: '12px',
                  '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}>
                Aceptar
              </Button>}
            </>
          )}
          {status === 'confirmada' && (
            <Button size="small" variant="outlined" disabled={loading}
              onClick={() => setExtraOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'warning.main', color: 'warning.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.12), borderColor: (t) => darken(t.palette.warning.main, 0.06) } }}>
              + Extra
            </Button>
          )}
          {canReport && (
            <Button size="small" variant="outlined" disabled={loading}
              startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDisputaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'error.main', color: 'error.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.25), borderColor: 'error.dark' } }}>
              Reportar problema
            </Button>
          )}
          {canComplete && !disputaAbierta && !pagoPendiente && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCompletarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'success.main', fontSize: '12px',
                '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
              Servicio realizado
            </Button>
          )}
          {canCancelConfirmada && (
            <Button size="small" variant="contained" color="error" disabled={loading} disableElevation
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCancelarConfirmadaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px' }}>
              Cancelar servicio
            </Button>
          )}
        </Box>
      </Box>

      <ModificarModal
        open={modificarOpen}
        onClose={() => setModificarOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor
        onConfirm={(id, fechaNueva, horaNueva) => {
          setModificarOpen(false);
          onModificar(id, fechaNueva, horaNueva);
        }}
      />

      <ReporteServicioModal
        open={disputaOpen}
        onClose={() => setDisputaOpen(false)}
        loading={loading}
        esProveedor
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onReportar(booking._id, payload);
          if (ok) setDisputaOpen(false);
        }}
      />

      <DetalleReservaModal
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        booking={booking}
        personaNombre={clientName}
        enRetencion={enRetencion}
        puedeReportar={dentroVentanaReporte(fecha)}
        onReportar={() => { setDetalleOpen(false); (enRetencion ? setDisputaOpen(true) : setAclaracionOpen(true)); }}
      />

      <ReporteServicioModal
        open={aclaracionOpen}
        onClose={() => setAclaracionOpen(false)}
        loading={loading}
        esProveedor
        modo="aclaracion"
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onAclarar(booking._id, payload);
          if (ok) setAclaracionOpen(false);
        }}
      />

      <DeclineModal
        open={declineOpen}
        onClose={() => setDeclineOpen(false)}
        loading={loading}
        onConfirm={(motivo) => {
          setDeclineOpen(false);
          onDecline(booking._id, motivo);
        }}
      />

      <AcceptModal
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        loading={loading}
        booking={booking}
        onConfirm={() => {
          setAcceptOpen(false);
          onAccept(booking._id);
        }}
      />

      <CompletarModal
        open={completarOpen}
        onClose={() => setCompletarOpen(false)}
        loading={loading}
        booking={booking}
        onConfirm={(pin) => {
          setCompletarOpen(false);
          onComplete(booking._id, pin);
        }}
      />

      <ExtraModal
        open={extraOpen}
        onClose={() => setExtraOpen(false)}
        loading={loading}
        onConfirm={(concepto, monto) => {
          setExtraOpen(false);
          onAddExtra(booking._id, concepto, monto);
        }}
      />

      <CancelarConfirmadaModal
        open={cancelConfirmadaOpen}
        onClose={() => setCancelarConfirmadaOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor={true}
        onConfirm={() => {
          setCancelarConfirmadaOpen(false);
          onDecline(booking._id, 'Cancelado por el proveedor');
        }}
      />
    </Box>
  );
};

// Estado del pago de una reserva: referencia SPEI/tienda pendiente, o confirmación
const PAGO_STATUS = {
  pendiente: { label: 'Pago pendiente', bgcolor: (t) => alpha(t.palette.warning.main, 0.3), color: 'warning.oscuro' },
  retenido: { label: 'Pagado', bgcolor: 'success.claro', color: 'primary.main' },
  liberado: { label: 'Pagado', bgcolor: 'success.claro', color: 'primary.main' },
  reembolsado: { label: 'Reembolsado', bgcolor: (t) => alpha(t.palette.secondary.main, 0.2), color: 'primary.main' },
  devolucion_pendiente: { label: 'Devolución en proceso', bgcolor: (t) => alpha(t.palette.warning.main, 0.3), color: 'warning.oscuro' },
};

// Fecha de vencimiento legible de una referencia
const fmtVence = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return ''; }
};

// Datos de una referencia OXXO/SPEI para que el cliente la pague
const ReferenciaBloque = ({ ref, metodo, monto }) => {
  if (!ref) return null;
  const Dato = ({ label, value, mono }) => (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: mono ? 15 : 13.5, fontFamily: mono ? 'monospace' : 'inherit', fontWeight: 700, color: 'primary.main', wordBreak: 'break-all' }}>{value || '—'}</Typography>
    </Box>
  );
  return (
    <Box sx={{ mt: 1.5, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: '12px', p: 2 }}>
      {metodo === 'spei' ? (
        <>
          <Dato label="Banco" value={ref.banco} />
          <Dato label="CLABE" value={ref.clabe} mono />
          {ref.referencia && <Dato label="Referencia / Concepto" value={ref.referencia} mono />}
          <Dato label="Monto exacto" value={`${dinero(monto)} MXN`} />
        </>
      ) : (
        <>
          <Dato label="Referencia OXXO" value={ref.referencia} mono />
          <Dato label="Monto" value={`${dinero(monto)} MXN`} />
        </>
      )}
      {ref.dueDate && (
        <Typography sx={{ fontSize: 11.5, color: 'warning.oscuro', fontWeight: 600, mt: 0.5 }}>
          Vence: {fmtVence(ref.dueDate)}
        </Typography>
      )}
      {ref.barcodeUrl && (
        <Button
          size="small" variant="outlined" href={ref.barcodeUrl} target="_blank" rel="noopener"
          sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
        >
          {metodo === 'oxxo' ? 'Ver / descargar ficha' : 'Ver instrucciones de pago'}
        </Button>
      )}
    </Box>
  );
};

const PagoDialog = ({ open, onClose, bookingId }) => {
  const [pago, setPago] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [verificando, setVerificando] = useState(false);
  const [verifMsg, setVerifMsg] = useState('');

  useEffect(() => {
    if (!open || !bookingId) return;
    setCargando(true);
    setVerifMsg('');
    getPagoBooking(bookingId).then((data) => {
      setPago(data);
      setCargando(false);
    });
  }, [open, bookingId]);

  const handleVerificar = async () => {
    setVerificando(true);
    setVerifMsg('');
    const data = await verificarPagoBooking(bookingId);
    setVerificando(false);
    if (data) {
      setPago(data);
      setVerifMsg(data.confirmado ? '✓ ¡Pago confirmado!' : 'Aún no vemos tu pago reflejado. Si acabas de pagar, puede tardar unos minutos.');
    } else {
      setVerifMsg('No pudimos verificar el pago en este momento. Intenta de nuevo.');
    }
  };

  const st = pago ? (PAGO_STATUS[pago.status] || null) : null;
  const refPendiente = pago && pago.status === 'pendiente' && pago.metodo !== 'card';
  const extraRefPendiente = pago?.extra && pago.extra.status === 'pendiente' && pago.extra.metodo !== 'card';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="900" color="primary.main" sx={{ mb: 2 }}>
          Datos de pago
        </Typography>

        {cargando ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={26} /></Box>
        ) : !pago ? (
          <Typography variant="body2" color="text.secondary">Esta reserva no requirió pago en línea.</Typography>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="900" color="primary.main">
                {dinero(pago.monto)} <Box component="small" sx={{ fontSize: 13, color: 'text.secondary' }}>{pago.moneda}</Box>
              </Typography>
              {st && (
                <Chip label={st.label} size="small"
                  sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, borderRadius: '6px' }} />
              )}
            </Box>

            {pago.status === 'pendiente' && pago.metodo === 'card' && (
              <Typography variant="body2" color="text.secondary">
                Tu tarjeta {pago.tarjeta?.last4 ? `terminación ${pago.tarjeta.last4} ` : ''}se cobrará cuando el
                proveedor acepte tu reserva.
              </Typography>
            )}

            {/* Referencia OXXO/SPEI del pago principal por pagar */}
            {refPendiente && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {pago.metodo === 'spei'
                    ? 'Haz la transferencia SPEI con estos datos. Tu lugar se aparta al confirmarse el pago.'
                    : 'Paga en efectivo en cualquier OXXO con esta ficha. Tu lugar se aparta al confirmarse el pago.'}
                </Typography>
                <ReferenciaBloque ref={pago.referenciaPago} metodo={pago.metodo} monto={pago.monto} />
                <Button
                  fullWidth variant="contained" disableElevation onClick={handleVerificar} disabled={verificando}
                  sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  {verificando ? 'Verificando…' : 'Ya pagué, verificar'}
                </Button>
              </Box>
            )}

            {['retenido', 'liberado'].includes(pago.status) && (
              <Typography variant="body2" color="text.secondary">
                Tu pago está confirmado{pago.metodo === 'card' && pago.tarjeta?.last4 ? ` (tarjeta terminación ${pago.tarjeta.last4})` : ''}.
                El monto queda protegido hasta que el servicio se complete.
              </Typography>
            )}

            {pago.status === 'devolucion_pendiente' && (
              <Typography variant="body2" color="text.secondary">
                Estamos procesando la devolución de tu pago. Te contactaremos para completarla.
              </Typography>
            )}

            {pago.status === 'reembolsado' && (
              <Typography variant="body2" color="text.secondary">
                Tu pago fue reembolsado a tu tarjeta. El banco puede tardar unos días en reflejarlo.
              </Typography>
            )}

            {/* Cobro de extras del servicio (si existe) */}
            {pago.extra && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'sutil' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    Extras del servicio — {dinero(pago.extra.monto)} {pago.extra.moneda}
                  </Typography>
                  {PAGO_STATUS[pago.extra.status] && (
                    <Chip label={PAGO_STATUS[pago.extra.status].label} size="small"
                      sx={{ bgcolor: PAGO_STATUS[pago.extra.status].bgcolor, color: PAGO_STATUS[pago.extra.status].color, fontWeight: 700, fontSize: 11, borderRadius: '6px' }} />
                  )}
                </Box>

                {pago.extra.status === 'fallido' && (
                  <Typography variant="caption" color="error.oscuro">
                    No pudimos cobrar los extras a tu tarjeta. Contacta al proveedor para acordar el pago.
                  </Typography>
                )}

                {/* Referencia OXXO/SPEI de los extras por pagar */}
                {extraRefPendiente && (
                  <>
                    <ReferenciaBloque ref={pago.extra.referenciaPago} metodo={pago.extra.metodo} monto={pago.extra.monto} />
                    {!refPendiente && (
                      <Button
                        fullWidth variant="contained" disableElevation onClick={handleVerificar} disabled={verificando}
                        sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                      >
                        {verificando ? 'Verificando…' : 'Ya pagué, verificar'}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}

            {verifMsg && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1.5, fontWeight: 700, color: verifMsg.startsWith('✓') ? 'success.oscuro' : 'warning.oscuro' }}>
                {verifMsg}
              </Typography>
            )}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cerrar</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const ClientBookingCard = ({ booking, tieneTicket, onCancel, onModificar, onResponderCambio, onPagarExtras, onReportar, onAclarar, loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modificarOpen, setModificarOpen] = useState(false);
  const [cancelConfirmadaOpen, setCancelarConfirmadaOpen] = useState(false);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [pagarExtrasOpen, setPagarExtrasOpen] = useState(false);
  const [disputaOpen, setDisputaOpen] = useState(false);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [aclaracionOpen, setAclaracionOpen] = useState(false);
  const { servicioNombre, fecha, hora, duracionMinutos, modalidad, nota, status, proveedor } = booking;
  const providerName = proveedor ? `${proveedor.name} ${proveedor.apellido}` : 'Proveedor';
  const avatarSrc = proveedor?.avatar
    ? `${urlImg}/users/${proveedor._id}/${proveedor.avatar}?v=${proveedor.versionAvatar || 0}`
    : null;

  const st = STATUS_COLOR[status] || STATUS_COLOR.pendiente;
  const isPending = status === 'pendiente';
  const today = hoyEnMexico();
  // En pendiente el cambio es directo (el proveedor aún no acepta); en confirmada es solicitud
  const canModify = ['pendiente', 'confirmada'].includes(status) && !booking.modificacion?.solicitada && fecha >= today;
  const cambioDelProveedor = booking.modificacion?.solicitadaPor === 'proveedor';

  // Política de cancelación (confirmadas): gratis la 1.ª hora tras reservar o si faltan
  // +24 h; después se retiene 30% para el proveedor; con menos de 3 h ya no se cancela
  const horasParaServicio = (instanteDeCita(fecha, hora) - Date.now()) / 36e5;
  const enGracia = booking.createdAt && Date.now() - new Date(booking.createdAt).getTime() <= 36e5;
  const cancelGratis = enGracia || horasParaServicio > 24;
  const canCancelConfirmada = status === 'confirmada' && horasParaServicio > 3;
  // Solo hay retención real si el pago principal ya está en custodia
  const penalizacion = !cancelGratis && booking.pago?.status === 'retenido'
    ? { pct: 30, monto: booking.pago.monto, devuelto: Math.round(booking.pago.monto * 70) / 100 }
    : null;

  // Disputa: cuando el servicio ya debió ocurrir y aún no se completa, o durante la
  // semana de retención (completado con el pago aún en custodia, sin dispersar).
  const disputaAbierta = !!booking.disputa?.abierta;
  const enRetencion = status === 'completada' && booking.pago?.status === 'retenido' && !disputaAbierta;
  // Solo se puede reportar/aclarar dentro de los últimos 3 meses (por fecha del servicio)
  const canReport = ((status === 'confirmada' && horasParaServicio <= 0) || enRetencion) && !disputaAbierta && dentroVentanaReporte(fecha);
  const esFinalizado = status === 'completada';
  const facturaEmitida = booking.factura?.estado === 'emitida';

  // Pago de extras: se pagan ANTES de que el proveedor pueda completar el servicio.
  // pagoExtra viene agregado del backend: pagado (suma confirmada) + enProceso (referencia sin pagar)
  const totalExtras = Math.round((booking.extras || []).reduce((a, e) => a + e.monto, 0) * 100) / 100;
  const extrasPagado = booking.pagoExtra?.pagado || 0;
  const extrasEnProceso = !!booking.pagoExtra?.enProceso;
  const extrasRestante = Math.round((totalExtras - extrasPagado) * 100) / 100;
  const principalPagado = booking.pago && ['retenido', 'liberado'].includes(booking.pago.status);
  const pagoPendiente = status === 'confirmada' && booking.pago?.status === 'pendiente';
  const puedePagarExtras = ['confirmada', 'completada'].includes(status)
    && principalPagado && extrasRestante > 0 && !extrasEnProceso;

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'sutil', p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight="900" color="primary.main">
            {servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={st.label} size="small"
            sx={{ bgcolor: st.bgcolor, color: st.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px' }} />
          {booking.pago && PAGO_STATUS[booking.pago.status] && (
            <Chip
              label={PAGO_STATUS[booking.pago.status].label}
              size="small"
              sx={{
                bgcolor: PAGO_STATUS[booking.pago.status].bgcolor,
                color: PAGO_STATUS[booking.pago.status].color,
                fontWeight: 700, fontSize: '0.7rem', borderRadius: '6px'
              }} />
          )}
        </Box>
      </Box>

      {nota && (
        <Typography variant="body2" color="text.secondary" sx={{
          mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          <strong>Tu mensaje: </strong>{nota}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {minutesToLabel(hora)} - {minutesToLabel(hora, duracionMinutos)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {modalidad === 'Remoto'
            ? <LaptopIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            : modalidad === 'Domicilio'
            ? <HomeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            : <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          <Typography variant="caption" color="text.secondary">{modalidad}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">{formatDate(fecha)}</Typography>
        </Box>
      </Box>

      <DireccionServicio booking={booking} />

      {/* Disputa abierta: el pago está congelado hasta que la plataforma resuelva */}
      {disputaAbierta && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.25), border: 1, borderColor: 'error.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="error.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            🚩 Disputa abierta {booking.disputa.abiertaPor === 'cliente' ? '(la abriste tú)' : '(la abrió el proveedor)'}
          </Typography>
          <Typography variant="caption" color="error.oscuro">
            Tu pago está congelado mientras el equipo de la plataforma revisa el caso.
            Te avisaremos la resolución.
          </Typography>
        </Box>
      )}

      {/* Aviso de auto-completado: hay 3 días para reportar problemas */}
      {canReport && (
        <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'sutil', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            ¿El servicio ya ocurrió? Si todo salió bien, comparte tu PIN al proveedor.
            Si algo salió mal, tienes <strong>3 días</strong> para reportar un problema;
            después el servicio se completa automáticamente y el pago se libera.
          </Typography>
        </Box>
      )}

      {/* Badge de modificación pendiente: propia (en espera) o del proveedor (responder) */}
      {booking.modificacion?.solicitada && !cambioDelProveedor && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Cambio de fecha en espera de aprobación
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Solicitaste: <Box component="strong" sx={{ color: 'warning.oscuro' }}>
              {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
            </Box>
          </Typography>
        </Box>
      )}

      {booking.modificacion?.solicitada && cambioDelProveedor && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 2, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 1 }}>
            ⚡ El proveedor propone cambiar la fecha
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Fecha actual</Typography>
              <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ display: 'block' }}>
                {formatDate(booking.fecha)} — {minutesToLabel(booking.hora)}
              </Typography>
            </Box>
            <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center', fontSize: '1.1rem' }}>→</Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">Fecha propuesta</Typography>
              <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block' }}>
                {formatDate(booking.modificacion.fechaNueva)} — {minutesToLabel(booking.modificacion.horaNueva)}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" color="error" disabled={loading}
              onClick={() => onResponderCambio(booking._id, false)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
              Rechazar cambio
            </Button>
            <Button size="small" variant="contained" disabled={loading}
              onClick={() => onResponderCambio(booking._id, true)}
              sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
              Aceptar cambio
            </Button>
          </Box>
          {isPending && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              El proveedor propuso esta fecha, así que al aceptarla la reserva queda
              confirmada de inmediato y se procesa tu pago.
            </Typography>
          )}
        </Box>
      )}

      {/* Con SPEI u OXXO sin pagar el PIN todavía no sale: sin pago no hay servicio */}
      {pagoPendiente && (
        <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.12), border: 1, borderColor: 'warning.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="warning.oscuro" sx={{ display: 'block', mb: 0.5 }}>
            ⏳ Falta tu pago
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Paga {dinero(booking.pago.monto)} con los datos que están en «Datos de pago»
            {booking.pago.vence ? ` antes del ${fmtVence(booking.pago.vence)}` : ''}: si no llega a tiempo, la reserva se cancela sola.
            Tu PIN aparece aquí cuando se confirme tu pago.
          </Typography>
        </Box>
      )}

      {/* PIN de confirmación: el cliente lo da al proveedor SOLO al terminar bien */}
      {status === 'confirmada' && booking.pinConfirmacion && !pagoPendiente && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), border: 1, borderColor: 'info.main', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Box sx={{ bgcolor: 'white', borderRadius: '8px', px: 2, py: 0.5, border: '1px dashed', borderColor: 'secondary.main' }}>
            <Typography sx={{ fontWeight: 900, fontSize: 22, letterSpacing: 4, color: 'primary.main' }}>
              {booking.pinConfirmacion}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            <strong>PIN de confirmación.</strong> Dáselo al proveedor únicamente cuando el servicio
            haya terminado a tu satisfacción — al compartirlo aceptas el servicio y sus extras.
            {extrasRestante > 0 && (
              <> <Box component="strong" sx={{ color: 'warning.oscuro' }}>Hay extras pendientes de pago: págalos abajo antes de dar tu PIN.</Box></>
            )}
          </Typography>
        </Box>
      )}

      {/* Extras agregados por el proveedor durante el servicio */}
      {booking.extras?.length > 0 && (
        <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'sutil', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
            Extras del servicio
          </Typography>
          {booking.extras.map((e) => (
            <Box key={e._id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">{e.concepto}</Typography>
              <Typography variant="caption" fontWeight={700} color="primary.main">{dinero(e.monto)}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 0.5, borderColor: 'sutil' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={700} color="primary.main">Total extras</Typography>
            <Typography variant="caption" fontWeight={900} color="primary.main">{dinero(totalExtras)}</Typography>
          </Box>

          {/* Estado del pago de extras: se pagan antes de que el proveedor finalice */}
          {booking.pago && extrasEnProceso && (
            <Typography variant="caption" color="warning.oscuro" sx={{ display: 'block', mt: 0.5 }}>
              ⏳ Tu pago de extras está en proceso. Paga la referencia desde <strong>Datos de pago</strong>;
              en cuanto se confirme, el proveedor podrá finalizar el servicio.
            </Typography>
          )}
          {booking.pago && !extrasEnProceso && extrasRestante <= 0 && (
            <Typography variant="caption" color="success.oscuro" sx={{ display: 'block', mt: 0.5 }}>
              ✓ Extras pagados. Cuando el servicio termine, comparte tu PIN al proveedor para finalizar.
            </Typography>
          )}
          {puedePagarExtras && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                Paga los extras para que el proveedor pueda finalizar el servicio.
              </Typography>
              <Button size="small" variant="contained" disabled={loading}
                onClick={() => setPagarExtrasOpen(true)}
                sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                  '&:hover': { bgcolor: 'success.dark' }, '&.Mui-disabled': { bgcolor: 'success.claro' } }}>
                Pagar extras ({dinero(extrasRestante)})
              </Button>
            </Box>
          )}
          {booking.pago && !principalPagado && extrasRestante > 0 && !extrasEnProceso && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Podrás pagar los extras en cuanto el pago de tu reserva esté confirmado.
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ mb: 1.5, borderColor: 'sutil' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box
          onClick={() => proveedor?._id && navigate(`/perfil/${proveedor._id}`)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: proveedor?._id ? 'pointer' : 'default',
            '&:hover .provider-name': proveedor?._id ? { textDecoration: 'underline' } : {}
          }}
        >
          <Typography variant="caption" color="text.secondary">Proveedor</Typography>
          <Avatar src={avatarSrc} sx={{ width: 28, height: 28, fontSize: '0.7rem' }}>
            {!avatarSrc && providerName.charAt(0)}
          </Avatar>
          <Typography className="provider-name" variant="body2" fontWeight="bold" color="primary.main">{providerName}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tieneTicket && (
            <Button size="small" variant="outlined"
              startIcon={<SupportAgentOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/user/tickets?booking=${booking._id}`)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px', borderColor: 'secondary.main', color: 'primary.main',
                '&:hover': { borderColor: 'secondary.dark', bgcolor: (t) => alpha(t.palette.secondary.main, 0.12) } }}>
              Ver ticket
            </Button>
          )}
          {esFinalizado ? (
            <>
              {facturaEmitida && <FacturaBotones factura={booking.factura} />}
              <Button size="small" variant="contained" disableElevation
                startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setDetalleOpen(true)}
                sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'primary.main', fontSize: '12px',
                  '&:hover': { bgcolor: 'primary.dark' } }}>
                Ver detalles
              </Button>
            </>
          ) : (
            <>
              {booking.pago && (
                <Button size="small" variant="outlined"
                  startIcon={<ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setPagoOpen(true)}
                  sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'info.main', color: 'primary.main', fontSize: '12px',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), borderColor: 'secondary.main' } }}>
                  Datos de pago
                </Button>
              )}
              <Button size="small" variant="outlined"
                startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                disabled={!proveedor?._id}
                onClick={() => dispatch(startChat(proveedor._id, booking.servicio, 'service'))}
                sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'success.main', color: 'success.oscuro', fontSize: '12px',
                  '&:hover': { bgcolor: (t) => alpha(t.palette.success.main, 0.1), borderColor: 'success.oscuro' } }}>
                Contactar proveedor
              </Button>
            </>
          )}
          {canModify && (
            <Button size="small" variant="contained" disabled={loading}
              startIcon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
              onClick={() => setModificarOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: 'warning.main', color: 'warning.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => darken(t.palette.warning.main, 0.06) }, '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.warning.main, 0.3) } }}>
              Modificar fecha
            </Button>
          )}
          {canReport && (
            <Button size="small" variant="outlined" disabled={loading}
              startIcon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDisputaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', borderColor: 'error.main', color: 'error.oscuro', fontSize: '12px',
                '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.25), borderColor: 'error.dark' } }}>
              Reportar problema
            </Button>
          )}
          {isPending && (
            <Button size="small" variant="contained" color="error" disabled={loading}
              startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setConfirmOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px',
                '&.Mui-disabled': { bgcolor: (t) => alpha(t.palette.error.main, 0.6) } }}>
              Cancelar reserva
            </Button>
          )}
          {canCancelConfirmada && (
            <Button size="small" variant="contained" color="error" disabled={loading} disableElevation
              startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setCancelarConfirmadaOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px' }}>
              Cancelar reserva
            </Button>
          )}
        </Box>
      </Box>

      <ModificarModal
        open={modificarOpen}
        onClose={() => setModificarOpen(false)}
        loading={loading}
        booking={booking}
        directo={isPending}
        onConfirm={(id, fechaNueva, horaNueva) => {
          setModificarOpen(false);
          onModificar(id, fechaNueva, horaNueva);
        }}
      />

      {/* Dialog confirmación cancelación */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'error.oscuro', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CancelOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
            ¿Cancelar esta reserva?
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 1 }}>
            <strong>{servicioNombre}</strong> — {formatDate(fecha)} a las {minutesToLabel(hora)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 3 }}>
            El proveedor será notificado de tu cancelación.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setConfirmOpen(false)}
              sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
              Volver
            </Button>
            <Button fullWidth variant="contained" color="error" disabled={loading}
              onClick={() => { setConfirmOpen(false); onCancel(booking._id); }}
              sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Sí, cancelar'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CancelarConfirmadaModal
        open={cancelConfirmadaOpen}
        onClose={() => setCancelarConfirmadaOpen(false)}
        loading={loading}
        booking={booking}
        esProveedor={false}
        penalizacion={penalizacion}
        onConfirm={() => {
          setCancelarConfirmadaOpen(false);
          onCancel(booking._id);
        }}
      />

      <PagoDialog open={pagoOpen} onClose={() => setPagoOpen(false)} bookingId={booking._id} />

      <ReporteServicioModal
        open={disputaOpen}
        onClose={() => setDisputaOpen(false)}
        loading={loading}
        esProveedor={false}
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onReportar(booking._id, payload);
          if (ok) setDisputaOpen(false);
        }}
      />

      <DetalleReservaModal
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        booking={booking}
        personaNombre={providerName}
        enRetencion={enRetencion}
        puedeReportar={dentroVentanaReporte(fecha)}
        onReportar={() => { setDetalleOpen(false); (enRetencion ? setDisputaOpen(true) : setAclaracionOpen(true)); }}
      />

      <ReporteServicioModal
        open={aclaracionOpen}
        onClose={() => setAclaracionOpen(false)}
        loading={loading}
        esProveedor={false}
        modo="aclaracion"
        booking={booking}
        onConfirm={async (payload) => {
          const ok = await onAclarar(booking._id, payload);
          if (ok) setAclaracionOpen(false);
        }}
      />

      {/* Confirmación del pago de extras (método heredado del pago principal) */}
      <Dialog open={pagarExtrasOpen} onClose={() => setPagarExtrasOpen(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          <Typography variant="h6" fontWeight="900" color="primary.main" textAlign="center" sx={{ mb: 1 }}>
            Pagar extras del servicio
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
            Total a pagar: <Box component="strong" sx={{ color: 'primary.main' }}>{dinero(extrasRestante)} MXN</Box>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mb: 3 }}>
            {booking.pago?.metodo === 'card'
              ? 'Se hará el cargo a tu tarjeta guardada en este momento.'
              : booking.pago?.metodo === 'spei'
              ? 'Se generará una referencia SPEI; el pago se confirma al recibir tu transferencia.'
              : 'Se generará una referencia para pagar en tienda; el pago se confirma al pagarla.'}
            {' '}Una vez pagados, el proveedor podrá finalizar el servicio.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setPagarExtrasOpen(false)}
              sx={{ textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700, borderColor: 'divider', color: 'text.secondary' }}>
              Volver
            </Button>
            <Button fullWidth variant="contained" disabled={loading}
              onClick={() => { setPagarExtrasOpen(false); onPagarExtras(booking._id); }}
              sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: '10px', py: 1.5, fontWeight: 700,
                '&:hover': { bgcolor: 'success.dark' } }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : booking.pago?.metodo === 'card' ? 'Pagar ahora' : 'Generar referencia'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const TABS = [
  { key: 'pendiente',  label: 'Solicitudes pendientes', statKey: 'pendiente' },
  { key: 'activo',     label: 'Servicios activos',      statKey: 'activo' },
  { key: 'proximo',    label: 'Servicios próximos',     statKey: 'proximo' },
  { key: 'finalizado', label: 'Finalizados',            statKey: 'finalizado' },
  { key: 'caducado',   label: 'Caducos',                statKey: 'caducado' },
  { key: 'cancelado',  label: 'Cancelados',             statKey: 'cancelado' }
];

// Pestañas históricas (pueden crecer sin límite) → muestran el filtro por periodo.
// Las de trabajo actual/futuro (pendiente/activo/próximo) no lo necesitan.
const TAB_CON_FILTRO = ['finalizado', 'caducado', 'cancelado'];

const HISTORIAL_ESTATUS = {
  pendiente: { label: 'Pendiente', bgcolor: 'warning.main', color: 'warning.oscuro' },
  activo:    { label: 'Activo',    bgcolor: 'success.claro', color: 'primary.main' },
  aceptado:  { label: 'Aceptado',  bgcolor: 'primary.main', color: 'white' },
  cobrado:   { label: 'Cobrado',   bgcolor: 'info.main', color: 'primary.main' },
  cancelado: { label: 'Cancelado', bgcolor: (t) => alpha(t.palette.error.main, 0.25), color: 'error.oscuro' },
  caducado:  { label: 'Caducado',  bgcolor: 'grey.100', color: 'text.secondary' }
};

const getHistorialEstatusKey = (booking) => {
  const today = hoyEnMexico();
  switch (booking.status) {
    case 'pendiente':  return 'pendiente';
    case 'confirmada': return booking.fecha === today ? 'activo' : 'aceptado';
    case 'completada': return 'cobrado';
    case 'cancelada':  return 'cancelado';
    case 'expirada':   return 'caducado';
    default:           return 'pendiente';
  }
};

// Convierte la duración de la reserva a la unidad de cobro del servicio (Hora / Día / Servicio)
const calcularUnidad = (booking) => {
  const mins = booking.duracionMinutos || 0;
  if (booking.tipoCosto === 'Hora') return Math.round((mins / 60) * 10) / 10;
  if (booking.tipoCosto === 'Día') return Math.round((mins / (60 * 24)) * 10) / 10;
  return 1;
};

const HistorialDetalleModal = ({ booking, vista, onClose }) => {
  if (!booking) return null;
  const otraParte = vista === 'proveedor' ? booking.cliente : booking.proveedor;
  const nombreOtraParte = otraParte ? `${otraParte.name} ${otraParte.apellido}` : 'NA';

  return (
    <Dialog open={!!booking} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ mb: 2 }}>
          {booking.servicioNombre || 'Detalle del servicio'}
        </Typography>
        <Stack spacing={1.4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">{vista === 'proveedor' ? 'Cliente' : 'Proveedor'}</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">{nombreOtraParte}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Fecha</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">{formatDate(booking.fecha)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Hora</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">{minutesToLabel(booking.hora)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Modalidad</Typography>
            <Typography variant="body2" fontWeight={600} color="text.primary">{booking.modalidad}</Typography>
          </Box>
          {booking.direccionServicio && (booking.direccionServicio.direccion || booking.direccionServicio.municipio) && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>Dirección del servicio</Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {booking.direccionServicio.nombre ? `${booking.direccionServicio.nombre}: ` : ''}
                {booking.direccionServicio.direccion
                  || [booking.direccionServicio.calle, booking.direccionServicio.colonia, booking.direccionServicio.municipio, booking.direccionServicio.estado]
                    .filter(Boolean).join(', ')}
              </Typography>
              {booking.direccionServicio.referencia && (
                <Typography variant="body2" color="text.secondary">Ref: {booking.direccionServicio.referencia}</Typography>
              )}
            </Box>
          )}
          {booking.nota && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>Nota</Typography>
              <Typography variant="body2" color="text.primary">{booking.nota}</Typography>
            </Box>
          )}
        </Stack>
        <Button fullWidth variant="contained" onClick={onClose}
          sx={{ mt: 3, bgcolor: 'primary.main', textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700,
            '&:hover': { bgcolor: 'primary.dark' } }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const HISTORIAL_COLUMNAS = [
  { label: 'Nombre',         flex: 1.4, align: 'left' },
  { label: '',               flex: 1.2, align: 'left' }, // Cliente / Proveedor — se etiqueta dinámicamente
  { label: 'Fecha',          flex: 1,   align: 'left' },
  { label: 'Estatus',        flex: 1,   align: 'center' },
  { label: 'Calificación',   flex: 1,   align: 'center' },
  { label: 'Costo x unidad', flex: 1,   align: 'center' },
  { label: 'Unidad',         flex: 0.7, align: 'center' },
  { label: 'Costo total',    flex: 1,   align: 'center' },
  { label: 'Detalles',       flex: 0.6, align: 'right' }
];

const HistorialRow = ({ booking, vista, isLast, onVer, onCalificar }) => {
  const estatus = HISTORIAL_ESTATUS[getHistorialEstatusKey(booking)];
  const unidad = calcularUnidad(booking);
  const costoUnidad = parseFloat(booking.costoServicio);
  const tieneCosto = !isNaN(costoUnidad);
  const otraParte = vista === 'proveedor' ? booking.cliente : booking.proveedor;
  const nombreOtraParte = otraParte ? `${otraParte.name} ${otraParte.apellido}` : 'NA';

  const calificacion = vista === 'proveedor'
    ? booking.calificacionProveedor
    : booking.calificacionCliente;
  const puedeCalificar = booking.status === 'completada' && !calificacion?.calificado;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.6, borderBottom: isLast ? 'none' : 1, borderColor: 'sutil' }}>
      <Box sx={{ flex: 1.4, pr: 1 }}>
        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap>{booking.servicioNombre || 'NA'}</Typography>
      </Box>
      <Box sx={{ flex: 1.2, pr: 1 }}>
        <Typography variant="body2" color="text.primary" noWrap>{nombreOtraParte}</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.primary" noWrap>{formatDate(booking.fecha)}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Chip label={estatus.label} size="small"
          sx={{ bgcolor: estatus.bgcolor, color: estatus.color, fontWeight: 700, borderRadius: '8px', minWidth: 84 }} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        {calificacion?.calificado ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            <Rating value={calificacion.rating} readOnly size="small" sx={{ color: 'primary.main', fontSize: '0.95rem' }} />
          </Box>
        ) : puedeCalificar ? (
          <Button size="small" onClick={() => onCalificar(booking)}
            sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 700, fontSize: '0.75rem', minWidth: 0, px: 1,
              border: 1, borderColor: 'secondary.main', borderRadius: '6px',
              '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.08) } }}>
            Calificar
          </Button>
        ) : (
          <Typography variant="body2" color="text.disabled">—</Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="text.primary">{tieneCosto ? dinero(costoUnidad) : 'NA'}</Typography>
      </Box>
      <Box sx={{ flex: 0.7, textAlign: 'center' }}>
        <Typography variant="body2" color="text.primary">{unidad}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography variant="body2" fontWeight={700} color="text.primary">
          {tieneCosto ? dinero(costoUnidad * unidad) : 'NA'}
        </Typography>
      </Box>
      <Box sx={{ flex: 0.6, textAlign: 'right' }}>
        <Button size="small" onClick={onVer}
          sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 700, fontSize: '0.8rem', minWidth: 0, p: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
          Ver
        </Button>
      </Box>
    </Box>
  );
};

const HistorialTable = ({ bookings, vista, onCalificar, actionLoading }) => {
  const [detalle, setDetalle] = useState(null);
  const [calificarBooking, setCalificarBooking] = useState(null);
  const otraParteLabel = vista === 'proveedor' ? 'Cliente' : 'Proveedor';

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.6, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        {HISTORIAL_COLUMNAS.map((c, i) => (
          <Box key={i} sx={{ flex: c.flex, textAlign: c.align }}>
            <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
              {c.label || otraParteLabel}
            </Typography>
          </Box>
        ))}
      </Box>
      {bookings.map((b, i) => (
        <HistorialRow
          key={b._id}
          booking={b}
          vista={vista}
          isLast={i === bookings.length - 1}
          onVer={() => setDetalle(b)}
          onCalificar={(booking) => setCalificarBooking(booking)}
        />
      ))}
      <HistorialDetalleModal booking={detalle} vista={vista} onClose={() => setDetalle(null)} />
      <CalificarModal
        open={!!calificarBooking}
        onClose={() => setCalificarBooking(null)}
        loading={actionLoading}
        esProveedor={vista === 'proveedor'}
        onConfirm={(rating, comentario) => {
          onCalificar(calificarBooking._id, rating, comentario);
          setCalificarBooking(null);
        }}
      />
    </Box>
  );
};

// ---- Filtros del Historial ----------------------------------------------
// Presets de tiempo (lo más visible) + estados seleccionables. Por defecto se
// muestran los últimos 3 meses y se excluyen canceladas/caducadas.
const HISTORIAL_TIEMPO_OPCIONES = [
  { key: '3m',    label: 'Últimos 3 meses', meses: 3 },
  { key: '6m',    label: '6 meses',         meses: 6 },
  { key: '1a',    label: '1 año',           meses: 12 },
  { key: 'rango', label: 'Personalizado',   meses: null }
];

const HISTORIAL_ESTADO_OPCIONES = [
  { key: 'pendiente',  label: 'Pendiente',  bgcolor: 'warning.main', color: 'warning.oscuro' },
  { key: 'confirmada', label: 'Confirmada', bgcolor: 'success.claro', color: 'primary.main' },
  { key: 'completada', label: 'Completada', bgcolor: 'info.main', color: 'primary.main' },
  { key: 'cancelada',  label: 'Cancelada',  bgcolor: (t) => alpha(t.palette.error.main, 0.25), color: 'error.oscuro' },
  { key: 'expirada',   label: 'Caducada',   bgcolor: 'grey.100', color: 'text.secondary' }
];

const HISTORIAL_ESTADOS_DEFAULT = ['pendiente', 'confirmada', 'completada'];

// n meses antes de hoy (en México). La fecha se arma en UTC, así que toISOString no la corre de día.
const isoRestarMeses = (n) => {
  const [anio, mes, dia] = hoyEnMexico().split('-').map(Number);
  return new Date(Date.UTC(anio, mes - 1 - n, dia)).toISOString().split('T')[0];
};

const fechaLarga = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

// Texto que le dice al usuario qué rango está viendo (lo más visible)
const textoRangoHistorial = (tiempo, desde, hasta) => {
  switch (tiempo) {
    case '3m': return 'Mostrando reservas de los últimos 3 meses';
    case '6m': return 'Mostrando reservas de los últimos 6 meses';
    case '1a': return 'Mostrando reservas del último año';
    case 'rango':
      if (desde && hasta) return `Mostrando del ${fechaLarga(desde)} al ${fechaLarga(hasta)}`;
      if (desde) return `Mostrando desde el ${fechaLarga(desde)}`;
      if (hasta) return `Mostrando hasta el ${fechaLarga(hasta)}`;
      return 'Elige un rango de fechas';
    default: return '';
  }
};

// Ventana en la que aún se puede reportar/aclarar un servicio: últimos 3 meses (por
// fecha del servicio). Los más viejos ya no muestran el botón de reportar.
const dentroVentanaReporte = (fecha) => !!fecha && fecha >= isoRestarMeses(3);

const HistorialFiltros = ({ titulo = 'Filtrar historial', mostrarEstatus = true, opciones = HISTORIAL_TIEMPO_OPCIONES, tiempo, onTiempo, desde, hasta, onDesde, onHasta, estados, onToggleEstado }) => {
  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', p: { xs: 2, md: 2.5 }, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <FilterAltOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={700} color="primary.main">{titulo}</Typography>
      </Stack>

      {/* Periodo — el filtro más visible */}
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.4, display: 'block', mb: 1 }}>
        Periodo
      </Typography>
      <ToggleButtonGroup
        value={tiempo}
        exclusive
        onChange={(_, val) => { if (val) onTiempo(val); }}
        sx={{
          flexWrap: 'wrap', gap: 1,
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid !important', borderColor: (t) => `${t.palette.divider} !important`, borderRadius: '999px !important',
            px: 2, py: 0.6, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
            color: 'text.secondary', bgcolor: 'grey.50',
            '&:hover': { bgcolor: 'grey.100' },
            '&.Mui-selected': {
              bgcolor: 'primary.main', color: 'white', borderColor: (t) => `${t.palette.primary.main} !important`,
              '&:hover': { bgcolor: 'primary.dark' }
            }
          }
        }}
      >
        {opciones.map(op => (
          <ToggleButton key={op.key} value={op.key}>{op.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Rango personalizado */}
      {tiempo === 'rango' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            type="date" label="Desde" size="small" value={desde}
            onChange={(e) => onDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
          <TextField
            type="date" label="Hasta" size="small" value={hasta}
            onChange={(e) => onHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </Stack>
      )}

      {mostrarEstatus && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Estatus */}
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.4, display: 'block', mb: 1 }}>
            Estatus
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {HISTORIAL_ESTADO_OPCIONES.map(op => {
              const activo = estados.includes(op.key);
              return (
                <Chip
                  key={op.key}
                  label={op.label}
                  onClick={() => onToggleEstado(op.key)}
                  variant={activo ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700, borderRadius: '8px', cursor: 'pointer',
                    bgcolor: activo ? op.bgcolor : 'transparent',
                    color: activo ? op.color : 'text.secondary',
                    border: 1, borderColor: activo ? 'transparent' : 'divider',
                    '&:hover': { bgcolor: activo ? op.bgcolor : 'grey.50', opacity: activo ? 0.9 : 1 }
                  }}
                />
              );
            })}
          </Stack>
        </>
      )}

      {/* Info de rango — lo más visible: qué está viendo */}
      <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 2 }}>
        <EventOutlinedIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {textoRangoHistorial(tiempo, desde, hasta)}
        </Typography>
      </Stack>
    </Box>
  );
};

const CalendarDetalleModal = ({ booking, onClose }) => {
  if (!booking) return null;
  const estatus = STATUS_COLOR[booking.status] || STATUS_COLOR.pendiente;
  const clientName = booking.cliente ? `${booking.cliente.name} ${booking.cliente.apellido}` : 'Cliente';

  return (
    <Dialog open={!!booking} onClose={onClose} maxWidth="xs" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '20px', p: 1 } } }}>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
            {booking.servicioNombre || 'Servicio'}
          </Typography>
          <Chip label={estatus.label} size="small"
            sx={{ bgcolor: estatus.bgcolor, color: estatus.color, fontWeight: 'bold', borderRadius: 1 }} />
        </Box>
        <Stack spacing={1.2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Cliente</Typography>
            <Typography variant="body2" fontWeight="bold" color="primary.main">{clientName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Fecha</Typography>
            <Typography variant="body2" color="primary.main">{formatDate(booking.fecha)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Hora</Typography>
            <Typography variant="body2" color="primary.main">{minutesToLabel(booking.hora)} — {minutesToLabel(booking.hora, booking.duracionMinutos)}</Typography>
          </Box>
        </Stack>
        <Button fullWidth variant="contained" onClick={onClose}
          sx={{ mt: 3, bgcolor: 'primary.main', textTransform: 'none', borderRadius: '10px', py: 1.2, fontWeight: 700, '&:hover': { bgcolor: 'primary.dark' } }}>
          Cerrar
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const ProviderCalendar = ({ bookings, month, onPrevMonth, onNextMonth }) => {
  const [detalle, setDetalle] = useState(null);

  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const monthLabel = `${MONTH_NAMES[monthIndex]} ${year}`;
  const firstDayOfMonth = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const today = new Date();
  const isToday = (dayNum) => dayNum === today.getDate() && monthIndex === today.getMonth() && year === today.getFullYear();

  const bookingsByDay = {};
  bookings.forEach(b => {
    const dayNum = Number((b.fecha || '').split('-')[2]);
    if (!dayNum) return;
    (bookingsByDay[dayNum] = bookingsByDay[dayNum] || []).push(b);
  });

  return (
    <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton size="small" onClick={onPrevMonth} sx={{ bgcolor: 'sutil' }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ minWidth: 120, textAlign: 'center' }}>
          {monthLabel}
        </Typography>
        <IconButton size="small" onClick={onNextMonth} sx={{ bgcolor: 'sutil', border: 1, borderColor: 'secondary.main', color: 'primary.main' }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={1}>
        {DAY_NAMES.map(d => (
          <Grid size={1.7} key={d} sx={{ textAlign: 'center', pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">{d}</Typography>
          </Grid>
        ))}

        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <Grid size={1.7} key={`empty-${i}`}>
            <Box sx={{ minHeight: 96, border: 1, borderColor: 'grey.50', borderRadius: '8px' }} />
          </Grid>
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dayNum = i + 1;
          const dayBookings = bookingsByDay[dayNum] || [];
          return (
            <Grid size={1.7} key={dayNum}>
              <Box sx={{
                minHeight: 96, borderRadius: '8px', p: 0.8,
                border: 1, borderColor: isToday(dayNum) ? 'secondary.main' : 'sutil',
                bgcolor: isToday(dayNum) ? ((t) => alpha(t.palette.secondary.main, 0.05)) : 'transparent'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>{dayNum}</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.3 }}>
                  {dayBookings.slice(0, 2).map(b => {
                    const estatus = STATUS_COLOR[b.status] || STATUS_COLOR.pendiente;
                    return (
                      <Box key={b._id} onClick={() => setDetalle(b)}
                        sx={{ bgcolor: estatus.bgcolor, color: estatus.color, borderRadius: '6px', px: 0.8, py: 0.4, cursor: 'pointer' }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 'bold', lineHeight: 1.3 }}>
                          {minutesToLabel(b.hora)}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {b.servicioNombre}
                        </Typography>
                      </Box>
                    );
                  })}
                  {dayBookings.length > 2 && (
                    <Typography sx={{ fontSize: '10px', color: 'text.secondary', pl: 0.5 }}>
                      +{dayBookings.length - 2} más
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <CalendarDetalleModal booking={detalle} onClose={() => setDetalle(null)} />
    </Box>
  );
};

const STATS_EMPTY = { pendiente: 0, activo: 0, proximo: 0, finalizado: 0, caducado: 0, cancelado: 0, cobrar: 0, recibir: 0, totalMes: 0 };

const ProviderBookings = () => {
  // Por defecto se entra como cliente; las notificaciones del proveedor llegan con ?vista=proveedor
  const [searchParams, setSearchParams] = useSearchParams();
  const [vista, setVista] = useState(searchParams.get('vista') === 'proveedor' ? 'proveedor' : 'cliente');
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(STATS_EMPTY);
  const [bookings, setBookings] = useState([]);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const [historial, setHistorial] = useState([]);
  const [historialPages, setHistorialPages] = useState(1);
  const [historialPage, setHistorialPage] = useState(1);
  const [historialLoading, setHistorialLoading] = useState(true);

  // Filtros del historial: periodo (default últimos 3 meses) y estados
  // (default sin canceladas/caducadas). El usuario puede ampliarlos.
  const [histTiempo, setHistTiempo] = useState('3m');
  const [histDesde, setHistDesde] = useState('');
  const [histHasta, setHistHasta] = useState('');
  const [histEstados, setHistEstados] = useState(HISTORIAL_ESTADOS_DEFAULT);

  // Traduce los filtros de la UI a los params que espera el backend
  const filtrosHistorial = useMemo(() => {
    const f = { estados: histEstados };
    if (histTiempo === 'rango') {
      if (histDesde) f.desde = histDesde;
      if (histHasta) f.hasta = histHasta;
    } else {
      const meses = histTiempo === '6m' ? 6 : histTiempo === '1a' ? 12 : 3;
      f.desde = isoRestarMeses(meses);
    }
    return f;
  }, [histTiempo, histDesde, histHasta, histEstados]);

  const toggleHistEstado = (key) => {
    setHistEstados(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Filtro por periodo de las pestañas históricas (Finalizados/Caducos/Cancelados).
  // Por defecto últimos 3 meses; con 6m/1año/Personalizado se ven los más viejos.
  const [tabTiempo, setTabTiempo] = useState('3m');
  const [tabDesde, setTabDesde] = useState('');
  const [tabHasta, setTabHasta] = useState('');

  const tabActualUsaFiltro = TAB_CON_FILTRO.includes(TABS[tab].key);

  const filtrosTab = useMemo(() => {
    const f = {};
    if (tabTiempo === 'rango') {
      if (tabDesde) f.desde = tabDesde;
      if (tabHasta) f.hasta = tabHasta;
    } else {
      const meses = tabTiempo === '6m' ? 6 : tabTiempo === '1a' ? 12 : 3;
      f.desde = isoRestarMeses(meses);
    }
    return f;
  }, [tabTiempo, tabDesde, tabHasta]);

  // Filtros que realmente se envían para la pestaña activa (vacío si no aplica)
  const filtrosTabActivos = tabActualUsaFiltro ? filtrosTab : {};

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);

  // Reservas para las que el usuario tiene un ticket propio → muestran "Ver ticket".
  // Se basa en los tickets reales del usuario (reportes Y aclaraciones), no en el
  // campo booking.disputa (que también incluye disputas viejas sin ticket).
  const [ticketBookingIds, setTicketBookingIds] = useState(() => new Set());
  useEffect(() => {
    getMisTickets()
      .then(ts => setTicketBookingIds(new Set((ts || []).map(t => t.booking?._id).filter(Boolean))))
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async (v) => {
    const data = v === 'proveedor' ? await getProviderStats() : await getClientStats();
    setStats(data || STATS_EMPTY);
  }, []);

  const fetchBookings = useCallback(async (v, tabKey, p, filtros = {}) => {
    setLoading(true);
    const data = v === 'proveedor'
      ? await getProviderBookings(tabKey, p, filtros)
      : await getClientBookings(tabKey, p, filtros);
    setBookings(data.bookings || []);
    setPages(data.pages || 1);
    setLoading(false);
  }, []);

  const fetchHistorial = useCallback(async (v, p, filtros) => {
    setHistorialLoading(true);
    const data = v === 'proveedor'
      ? await getProviderBookings('historial', p, filtros)
      : await getClientBookings('historial', p, filtros);
    setHistorial(data.bookings || []);
    setHistorialPages(data.pages || 1);
    setHistorialLoading(false);
  }, []);

  // Si llega ?vista= estando ya en la página (p. ej. desde una notificación), sincroniza
  useEffect(() => {
    const v = searchParams.get('vista');
    if (v === 'proveedor' || v === 'cliente') {
      setVista(v);
      setTab(0);
      setPage(1);
    }
  }, [searchParams]);

  // La reserva de un aviso (?reserva=<id>): se busca entre las tuyas como proveedor y como cliente (el servidor solo da las propias),
  // sale primero con sus botones y la vista cambia a la que le toca
  const reservaId = searchParams.get('reserva');
  const [reservaAviso, setReservaAviso] = useState(null);   // { booking, vista } | { noEncontrada: true } | null
  const cargarReservaAviso = useCallback(async () => {
    if (!reservaId) {
      setReservaAviso(null);
      return null;
    }
    // Solo la que tiene ese id: un servidor sin actualizar ignora ?reserva= y contesta con la lista de pendientes
    const deEsa = (data) => (data.bookings || []).find(b => String(b._id) === String(reservaId));
    const [comoProveedor, comoCliente] = await Promise.all([
      getProviderBookings('pendiente', 1, { reserva: reservaId }).then(deEsa),
      getClientBookings('pendiente', 1, { reserva: reservaId }).then(deEsa),
    ]);
    const encontrada = comoProveedor ? { booking: comoProveedor, vista: 'proveedor' }
      : comoCliente ? { booking: comoCliente, vista: 'cliente' }
        : { noEncontrada: true };
    setReservaAviso(encontrada);
    return encontrada;
  }, [reservaId]);

  useEffect(() => {
    cargarReservaAviso().then((r) => {
      if (r?.vista) {
        setVista(r.vista);
        setTab(0);
        setPage(1);
      }
    });
  }, [cargarReservaAviso]);

  // Al terminar cualquier botón (aceptar, cancelar, pagar…) se vuelve a pedir, para que se vea su nuevo estado
  const habiaAccion = useRef(false);
  useEffect(() => {
    if (habiaAccion.current && !actionLoading) cargarReservaAviso();
    habiaAccion.current = actionLoading;
  }, [actionLoading, cargarReservaAviso]);

  const quitarReservaAviso = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('reserva');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    fetchStats(vista);
  }, [vista, fetchStats]);

  useEffect(() => {
    setPage(1);
    const usa = TAB_CON_FILTRO.includes(TABS[tab].key);
    fetchBookings(vista, TABS[tab].key, 1, usa ? filtrosTab : {});
  }, [vista, tab, filtrosTab, fetchBookings]);

  useEffect(() => {
    setHistorialPage(1);
    fetchHistorial(vista, 1, filtrosHistorial);
  }, [vista, filtrosHistorial, fetchHistorial]);

  // Al cambiar de página subimos al inicio de la sección (con margen para el header
  // fijo) para que la nueva página no quede "perdida" abajo si tiene pocos elementos.
  const tabsRef = useRef(null);
  const historialRef = useRef(null);
  const scrollAInicio = (ref) => {
    const el = ref.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleHistorialPageChange = (_, val) => {
    setHistorialPage(val);
    fetchHistorial(vista, val, filtrosHistorial);
    scrollAInicio(historialRef);
  };

  const fetchCalendar = useCallback(async (date) => {
    setCalendarLoading(true);
    const data = await getProviderCalendar(date.getMonth(), date.getFullYear());
    setCalendarBookings(data || []);
    setCalendarLoading(false);
  }, []);

  useEffect(() => {
    if (vista !== 'proveedor') return;
    fetchCalendar(calendarMonth);
  }, [vista, calendarMonth, fetchCalendar]);

  const handlePrevCalendarMonth = () => {
    setCalendarMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };
  const handleNextCalendarMonth = () => {
    setCalendarMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const handleVista = (v) => {
    if (v === vista) return;
    setVista(v);
    setTab(0);
    setPage(1);
  };

  const handlePageChange = (_, val) => {
    setPage(val);
    fetchBookings(vista, TABS[tab].key, val, filtrosTabActivos);
    scrollAInicio(tabsRef);
  };

  const handleAction = async (bookingId, status, motivo = '') => {
    setActionLoading(true);
    const result = await updateBookingStatus(bookingId, status, motivo);
    setActionLoading(false);
    if (result?.success) {
      const msg = status === 'confirmada' ? 'Reserva confirmada y cliente notificado.' : 'Reserva declinada. El cliente fue notificado.';
      setToast({ open: true, msg, severity: status === 'confirmada' ? 'success' : 'warning' });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      // El backend puede devolver un motivo específico (p. ej. cobro de tarjeta rechazado)
      setToast({ open: true, msg: result?.msg || 'Error al actualizar. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleSolicitarCambio = async (bookingId, fechaNueva, horaNueva) => {
    setActionLoading(true);
    const result = await solicitarCambio(bookingId, fechaNueva, horaNueva);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.aplicado
        ? 'Fecha actualizada. El proveedor fue notificado del cambio.'
        : vista === 'proveedor'
        ? 'Propuesta enviada. El cliente decidirá el cambio.'
        : 'Solicitud enviada. El proveedor decidirá el cambio.';
      setToast({ open: true, msg, severity: result.aplicado ? 'success' : 'info' });
      if (result.aplicado) {
        fetchStats(vista);
        fetchHistorial(vista, historialPage, filtrosHistorial);
      }
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al enviar la solicitud. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleResponderCambio = async (bookingId, aceptar) => {
    setActionLoading(true);
    const result = await responderCambio(bookingId, aceptar);
    setActionLoading(false);
    if (result?.success) {
      // confirmada llega cuando el cliente acepta la propuesta del proveedor en una
      // reserva pendiente: true = se confirmó en automático, false = falló el cobro
      let msg = aceptar ? 'Cambio aceptado. La reserva se actualizó.' : 'Cambio rechazado. La reserva mantiene su fecha original.';
      let severity = aceptar ? 'success' : 'warning';
      if (result.confirmada === true) {
        msg = 'Aceptaste la nueva fecha y la reserva quedó confirmada.';
      } else if (result.confirmada === false) {
        msg = `Aceptaste la nueva fecha, pero la reserva sigue pendiente: ${result.msgPago || 'no se pudo procesar el pago.'}`;
        severity = 'warning';
      }
      setToast({ open: true, msg, severity });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al responder. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleComplete = async (bookingId, pin = '') => {
    setActionLoading(true);
    const result = await completeBooking(bookingId, pin);
    setActionLoading(false);
    if (result?.success) {
      // El pago queda retenido (ventana de protección) y se libera en el corte semanal
      let msg = 'Servicio marcado como completado. Se reflejó en tus estadísticas.';
      let severity = 'success';
      if (result.pago === 'retenido') {
        msg = 'Servicio completado. Tu pago queda retenido ~1 semana (por si hay reportes) y se libera en el corte semanal si todo está en orden.';
      }
      setToast({ open: true, msg, severity });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
      fetchCalendar(calendarMonth);
    } else {
      // p. ej. PIN incorrecto
      setToast({ open: true, msg: result?.msg || 'Error al completar el servicio. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleAddExtra = async (bookingId, concepto, monto) => {
    setActionLoading(true);
    const result = await addExtra(bookingId, concepto, monto);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Extra agregado. El cliente ya puede verlo en su reserva.', severity: 'success' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo agregar el extra.', severity: 'error' });
    }
  };

  const handleReportarProblema = async (bookingId, payload) => {
    setActionLoading(true);
    const result = await reportarProblema(bookingId, payload);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Reporte enviado. El pago queda congelado mientras revisamos el caso; te avisaremos la resolución.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      return true;
    }
    setToast({ open: true, msg: result?.msg || 'No se pudo enviar el reporte. Intenta de nuevo.', severity: 'error' });
    return false;
  };

  const handleAclaracion = async (bookingId, payload) => {
    setActionLoading(true);
    const result = await crearAclaracion(bookingId, payload);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Aclaración enviada. Nuestro equipo de soporte revisará el caso y te contactará.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      return true;
    }
    setToast({ open: true, msg: result?.msg || 'No se pudo enviar la aclaración. Intenta de nuevo.', severity: 'error' });
    return false;
  };

  const handlePagarExtras = async (bookingId) => {
    setActionLoading(true);
    const result = await pagarExtras(bookingId);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.resultado === 'cobrado'
        ? 'Extras pagados. Cuando el servicio termine, comparte tu PIN al proveedor para finalizar.'
        : 'Se generó tu referencia de pago. Revisa "Datos de pago" para pagarla.';
      setToast({ open: true, msg, severity: result.resultado === 'cobrado' ? 'success' : 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo procesar el pago de los extras.', severity: 'error' });
    }
  };

  const handleRemoveExtra = async (bookingId, extraId) => {
    setActionLoading(true);
    const result = await removeExtra(bookingId, extraId);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: 'Extra eliminado.', severity: 'info' });
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
    } else {
      setToast({ open: true, msg: result?.msg || 'No se pudo quitar el extra.', severity: 'error' });
    }
  };

  const handleCalificar = async (bookingId, rating, comentario) => {
    setActionLoading(true);
    const result = await submitReview(bookingId, rating, comentario);
    setActionLoading(false);
    if (result?.success) {
      setToast({ open: true, msg: '¡Gracias por tu calificación!', severity: 'success' });
      fetchHistorial(vista, historialPage, filtrosHistorial);
    } else {
      setToast({ open: true, msg: 'Error al enviar la calificación. Intenta de nuevo.', severity: 'error' });
    }
  };

  const handleClientCancel = async (bookingId) => {
    setActionLoading(true);
    const result = await cancelClientBooking(bookingId);
    setActionLoading(false);
    if (result?.success) {
      const msg = result.penalizacionPct > 0
        ? `Reserva cancelada con penalización del ${result.penalizacionPct}%. El resto de tu pago se reembolsará.`
        : 'Reserva cancelada. El proveedor fue notificado.';
      setToast({ open: true, msg, severity: 'warning' });
      fetchStats(vista);
      fetchBookings(vista, TABS[tab].key, page, filtrosTabActivos);
      fetchHistorial(vista, historialPage, filtrosHistorial);
    } else {
      setToast({ open: true, msg: result?.msg || 'Error al cancelar. Intenta de nuevo.', severity: 'error' });
    }
  };

  // Tarjeta de una reserva con sus botones: la misma en la lista y en «La reserva de tu aviso»
  const tarjetaReserva = (b, v) => (
    v === 'proveedor'
      ? <BookingCard
          key={b._id}
          booking={b}
          tieneTicket={ticketBookingIds.has(b._id)}
          loading={actionLoading}
          onAccept={(id) => handleAction(id, 'confirmada')}
          onDecline={(id, motivo) => handleAction(id, 'cancelada', motivo)}
          onComplete={handleComplete}
          onModificar={handleSolicitarCambio}
          onResponderCambio={handleResponderCambio}
          onAddExtra={handleAddExtra}
          onRemoveExtra={handleRemoveExtra}
          onReportar={handleReportarProblema}
          onAclarar={handleAclaracion}
        />
      : <ClientBookingCard
          key={b._id}
          booking={b}
          tieneTicket={ticketBookingIds.has(b._id)}
          onCancel={handleClientCancel}
          onModificar={handleSolicitarCambio}
          onResponderCambio={handleResponderCambio}
          onPagarExtras={handlePagarExtras}
          onReportar={handleReportarProblema}
          onAclarar={handleAclaracion}
          loading={actionLoading}
        />
  );

  return (
    <Box className="main-page">
      {/* Header */}
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3, position: 'relative' }}>
        <Typography variant="h5">
          {vista === 'proveedor' ? 'Reservas como proveedor' : 'Servicios que contraté'}
        </Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>{vista === 'proveedor' ? 'Gestiona las solicitudes de tus clientes' : 'Revisa los servicios que has contratado'}</small>
        </Typography>

        {/* Toggle cliente / proveedor: arriba a la derecha en pantallas grandes; en las angostas tapaba el título (hasta ~600 px),
            así que ahí va debajo del texto */}
        <Box sx={{
          position: { xs: 'static', md: 'absolute' }, top: 24, right: 24, mt: { xs: 2.5, md: 0 },
          display: 'inline-flex', bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '10px', p: 0.5
        }}>
          <Button
            size="small"
            startIcon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleVista('cliente')}
            sx={{
              textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, px: 1.5,
              color: 'primary.main',
              bgcolor: vista === 'cliente' ? 'white' : 'transparent',
              '&:hover': { bgcolor: vista === 'cliente' ? 'white' : 'rgba(255,255,255,0.2)' }
            }}>
            Cliente
          </Button>
          <Button
            size="small"
            startIcon={<WorkOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={() => handleVista('proveedor')}
            sx={{
              textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, px: 1.5,
              color: 'primary.main',
              bgcolor: vista === 'proveedor' ? 'white' : 'transparent',
              '&:hover': { bgcolor: vista === 'proveedor' ? 'white' : 'rgba(255,255,255,0.2)' }
            }}>
            Proveedor
          </Button>
        </Box>
      </Box>

      {/* Como proveedor, si todavía no puede recibir sus pagos */}
      {vista === 'proveedor' && <AvisoCobro sx={{ mb: 3 }} />}

      <Box>
        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Servicios contratados (como cliente va solo, sin el recuadro de pagos, y ocupa todo el ancho) */}
          <Grid size={{ xs: 12, md: vista === 'proveedor' ? 6 : 12 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 0.3 }}>
              Servicios contratados
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Solicitudes actualizadas
            </Typography>
            <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', p: 2.5, display: 'flex' }}>
              {CONTRATADOS_STATS.map((s, i) => (
                <Box key={s.key} sx={{
                  flex: 1, textAlign: 'center', px: 1,
                  borderRight: i < CONTRATADOS_STATS.length - 1 ? 1 : 'none', borderColor: 'divider'
                }}>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color="text.primary"
                    sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }, whiteSpace: 'nowrap' }}
                  >
                    {formatCompactNumber(stats[s.key])}
                  </Typography>
                  <Box sx={{ height: 4, bgcolor: s.color, borderRadius: 2, mt: 0.8, mb: 0.6 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Pagos: el servidor solo calcula estas cifras para el proveedor (/provider/stats), con el precio de sus reservas; como
              cliente salían siempre en $0 */}
          {vista === 'proveedor' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 0.3 }}>
                Pagos
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Con el precio de tus reservas, antes de la comisión de la plataforma.
              </Typography>
              <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', p: 2.5, display: 'flex' }}>
                {getPagosStats(stats).map((s, i) => (
                  <Box key={s.key} sx={{
                    flex: 1, textAlign: 'center', px: 1,
                    borderRight: i < getPagosStats(stats).length - 1 ? 1 : 'none', borderColor: 'divider'
                  }}>
                    <Typography
                      variant="h4"
                      fontWeight="700"
                      color="text.primary"
                      sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' }, whiteSpace: 'nowrap' }}
                    >
                      {s.esMoneda ? formatCompactCurrency(s.value) : formatCompactNumber(s.value)}
                    </Typography>
                    <Box sx={{ height: 4, bgcolor: s.color, borderRadius: 2, mt: 0.8, mb: 0.6 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>

        {/* La reserva de un aviso (?reserva=): primero, con sus botones */}
        {reservaAviso?.booking && (
          <Box component="section" aria-label="La reserva de tu aviso"
            sx={{ bgcolor: 'white', borderRadius: '20px', border: '2px solid', borderColor: 'primary.main', p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">La reserva de tu aviso</Typography>
              <Button size="small" onClick={quitarReservaAviso} sx={{ textTransform: 'none' }}>Ver todas mis reservas</Button>
            </Box>
            {tarjetaReserva(reservaAviso.booking, reservaAviso.vista)}
          </Box>
        )}
        {reservaAviso?.noEncontrada && (
          <Alert severity="info" onClose={quitarReservaAviso} sx={{ mb: 3 }}>
            No encontramos esa reserva entre las tuyas.
          </Alert>
        )}

        {/* Tabs */}
        <Box ref={tabsRef} sx={{ bgcolor: 'white', borderRadius: '20px', border: 1, borderColor: 'sutil', overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: 1, borderColor: 'sutil',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, color: 'text.secondary', fontSize: '0.85rem' },
              '& .Mui-selected': { color: 'primary.main' },
              '& .MuiTabs-indicator': { bgcolor: 'primary.main', height: 3, borderRadius: '3px 3px 0 0' }
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={t.key} label={`${t.label} (${stats[t.statKey] ?? 0})`} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tabActualUsaFiltro && (
              <HistorialFiltros
                titulo="Filtrar por periodo"
                mostrarEstatus={false}
                tiempo={tabTiempo}
                onTiempo={setTabTiempo}
                desde={tabDesde}
                hasta={tabHasta}
                onDesde={setTabDesde}
                onHasta={setTabHasta}
              />
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : bookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {tabActualUsaFiltro
                    ? 'No hay reservas en este periodo. Prueba ampliarlo o usar un rango personalizado.'
                    : 'No hay reservas en esta categoría.'}
                </Typography>
              </Box>
            ) : (
              <>
                {bookings.map(b => tarjetaReserva(b, vista))}

                {pages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={pages}
                      page={page}
                      onChange={handlePageChange}
                      sx={{
                        '& .MuiPaginationItem-root': { borderRadius: '8px' },
                        '& .Mui-selected': { bgcolor: (t) => `${t.palette.primary.main} !important`, color: 'white' }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {vista === 'proveedor' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 0.3 }}>
              Calendario
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Servicios pactados (confirmados) y solicitudes pendientes por aceptar
            </Typography>

            {calendarLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ProviderCalendar
                bookings={calendarBookings}
                month={calendarMonth}
                onPrevMonth={handlePrevCalendarMonth}
                onNextMonth={handleNextCalendarMonth}
              />
            )}
          </Box>
        )}

        {/* Historial */}
        <Box ref={historialRef} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 0.3 }}>
            Historial
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {vista === 'proveedor'
              ? 'Reservas recibidas. Por defecto se muestran los últimos 3 meses (sin canceladas ni caducadas).'
              : 'Servicios que has contratado. Por defecto se muestran los últimos 3 meses (sin canceladas ni caducadas).'}
          </Typography>

          <HistorialFiltros
            tiempo={histTiempo}
            onTiempo={setHistTiempo}
            desde={histDesde}
            hasta={histHasta}
            onDesde={setHistDesde}
            onHasta={setHistHasta}
            estados={histEstados}
            onToggleEstado={toggleHistEstado}
          />

          {historialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : historial.length === 0 ? (
            <Box sx={{ bgcolor: 'white', borderRadius: '16px', border: 1, borderColor: 'divider', textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">
                No hay reservas con los filtros seleccionados.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Prueba ampliar el periodo o incluir más estatus.
              </Typography>
            </Box>
          ) : (
            <>
              <HistorialTable bookings={historial} vista={vista} onCalificar={handleCalificar} actionLoading={actionLoading} />

              {historialPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={historialPages}
                    page={historialPage}
                    onChange={handleHistorialPageChange}
                    sx={{
                      '& .MuiPaginationItem-root': { borderRadius: '8px' },
                      '& .Mui-selected': { bgcolor: (t) => `${t.palette.primary.main} !important`, color: 'white' }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}
          sx={{ borderRadius: '12px', fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderBookings;
